import { useCallback, useEffect, useRef, useState } from "react";

interface MetronomeState {
  isPlaying: boolean;
  bpm: number;
  beat: number;
  beatsPerMeasure: number;
}

export function useMetronome() {
  const [state, setState] = useState<MetronomeState>({
    isPlaying: false,
    bpm: 60,
    beat: 0,
    beatsPerMeasure: 4,
  });

  const audioContextRef = useRef<AudioContext | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const playClick = useCallback((isAccent: boolean) => {
    if (!audioContextRef.current) {
      audioContextRef.current = new AudioContext();
    }
    const ctx = audioContextRef.current;

    const osc = ctx.createOscillator();
    const gain = ctx.createGain();

    osc.connect(gain);
    gain.connect(ctx.destination);

    if (isAccent) {
      osc.frequency.value = 1000;
      gain.gain.value = 0.5;
    } else {
      osc.frequency.value = 700;
      gain.gain.value = 0.3;
    }

    osc.start(ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.05);
    osc.stop(ctx.currentTime + 0.05);
  }, []);

  const start = useCallback(() => {
    if (intervalRef.current) return;

    const intervalMs = (60 / state.bpm) * 1000;
    let currentBeat = 0;

    playClick(true); // First beat accent

    intervalRef.current = setInterval(() => {
      currentBeat = (currentBeat + 1) % state.beatsPerMeasure;
      setState((prev) => ({ ...prev, beat: currentBeat }));
      playClick(currentBeat === 0);
    }, intervalMs);

    setState((prev) => ({ ...prev, isPlaying: true, beat: 0 }));
  }, [state.bpm, state.beatsPerMeasure, playClick]);

  const stop = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    setState((prev) => ({ ...prev, isPlaying: false, beat: 0 }));
  }, []);

  const setBpm = useCallback((bpm: number) => {
    setState((prev) => {
      if (prev.isPlaying) {
        // Restart with new BPM
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
        }
        const intervalMs = (60 / bpm) * 1000;
        let currentBeat = prev.beat;
        intervalRef.current = setInterval(() => {
          currentBeat = (currentBeat + 1) % prev.beatsPerMeasure;
          setState((p) => ({ ...p, beat: currentBeat }));
          playClick(currentBeat === 0);
        }, intervalMs);
      }
      return { ...prev, bpm };
    });
  }, [playClick]);

  useEffect(() => {
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
      if (audioContextRef.current) {
        audioContextRef.current.close();
      }
    };
  }, []);

  return {
    ...state,
    start,
    stop,
    setBpm,
  };
}
